import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useAppContext } from '../../context/AppContext';
import { useAppStoreData } from '../../hooks/useAppStoreData';
import { fonts } from '../../styles/GlobalStyles';

// Everything is sized in cqw so a layer scales with the phone it collapses into.


/* ---------- Interface: the screen people actually see ---------- */

// App Store "Today"-style screen built from real data: current role, shipped apps, their categories.
const ios = {
  light: {
    bg: '#f2f2f7',
    card: '#ffffff',
    label: '#000000',
    secondary: 'rgba(60, 60, 67, 0.6)',
    separator: 'rgba(60, 60, 67, 0.16)',
    fill: 'rgba(118, 118, 128, 0.12)',
    bar: 'rgba(249, 249, 249, 0.94)',
  },
  dark: {
    bg: '#000000',
    card: '#1c1c1e',
    label: '#ffffff',
    secondary: 'rgba(235, 235, 245, 0.6)',
    separator: 'rgba(84, 84, 88, 0.55)',
    fill: 'rgba(118, 118, 128, 0.24)',
    bar: 'rgba(22, 22, 24, 0.94)',
  },
};

type Palette = typeof ios.light;

// App Store categories as the Turkish storefront shows them.
const genresTr: Record<string, string> = {
  Entertainment: 'Eğlence',
  News: 'Haberler',
  Business: 'İş',
  Productivity: 'Verimlilik',
  Utilities: 'Araçlar',
  Sports: 'Spor',
  Lifestyle: 'Yaşam Tarzı',
  Finance: 'Finans',
  Travel: 'Seyahat',
  'Social Networking': 'Sosyal Ağ',
};

const Screen = styled.div<{ $c: Palette }>`
  position: absolute;
  inset: 0;
  padding: 13.5cqw 4.6cqw 0;
  background: ${props => props.$c.bg};
  color: ${props => props.$c.label};
  font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Schibsted Grotesk', sans-serif;
  overflow: hidden;
`;

const Header = styled.div<{ $c: Palette }>`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin-bottom: 3.6cqw;

  small {
    display: block;
    font-size: 3.05cqw;
    font-weight: 600;
    letter-spacing: 0.02em;
    color: ${props => props.$c.secondary};
  }

  h4 {
    margin-top: 0.4cqw;
    font-size: 8.6cqw;
    font-weight: 700;
    letter-spacing: -0.025em;
    line-height: 1.05;
  }

  img {
    width: 9.4cqw;
    height: 9.4cqw;
    border-radius: 50%;
    object-fit: cover;
    margin-bottom: 0.6cqw;
  }
`;

const Feature = styled.div`
  position: relative;
  height: 70cqw;
  border-radius: 4.6cqw;
  overflow: hidden;
  color: #fff;
  background: #120a14 url('/images/office-1200.jpg') 50% 50% / cover;
  box-shadow: 0 2cqw 6cqw rgba(0, 0, 0, 0.18);

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(180deg, rgba(0, 0, 0, 0.55) 0%, rgba(0, 0, 0, 0) 45%);
  }
`;

const FeatureText = styled.div`
  position: relative;
  padding: 4.4cqw 4.6cqw;

  small {
    display: block;
    font-size: 2.9cqw;
    font-weight: 600;
    letter-spacing: 0.04em;
    opacity: 0.72;
  }

  strong {
    display: block;
    margin-top: 0.8cqw;
    max-width: 80%;
    font-size: 6.6cqw;
    font-weight: 700;
    line-height: 1.08;
    letter-spacing: -0.015em;
  }

  span {
    display: block;
    margin-top: 1.2cqw;
    overflow: hidden;
    font-size: 3.2cqw;
    white-space: nowrap;
    text-overflow: ellipsis;
    opacity: 0.8;
  }
`;

const FeatureApp = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  gap: 2.6cqw;
  padding: 3cqw 4cqw;
  background: rgba(20, 20, 22, 0.62);
  border-top: 1px solid rgba(255, 255, 255, 0.08);
`;

const Icon = styled.i<{ $size: number }>`
  width: ${props => props.$size}cqw;
  height: ${props => props.$size}cqw;
  flex-shrink: 0;
  border-radius: ${props => props.$size * 0.225}cqw;
  background: #fff center / cover;
  box-shadow: inset 0 0 0 1px rgba(127, 127, 127, 0.18);
`;

const Meta = styled.div<{ $c?: Palette }>`
  flex: 1;
  min-width: 0;

  strong,
  span {
    display: block;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  strong {
    font-size: 3.7cqw;
    font-weight: 600;
  }

  span {
    margin-top: 0.3cqw;
    font-size: 3cqw;
    color: ${props => (props.$c ? props.$c.secondary : 'rgba(255, 255, 255, 0.7)')};
  }
`;

const Get = styled.span<{ $fill: string }>`
  flex-shrink: 0;
  padding: 1.3cqw 3.8cqw;
  border-radius: 999px;
  font-size: 3.2cqw;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: ${props => props.theme.colors.primary};
  background: ${props => props.$fill};
`;

const SectionHead = styled.div<{ $c: Palette }>`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin: 5.4cqw 0 1.2cqw;
  padding-top: 4cqw;
  border-top: 1px solid ${props => props.$c.separator};

  strong {
    font-size: 5.2cqw;
    font-weight: 700;
    letter-spacing: -0.01em;
  }

  span {
    font-size: 3.4cqw;
    color: ${props => props.theme.colors.primary};
  }
`;

const AppRow = styled.div<{ $c: Palette }>`
  position: relative;
  display: flex;
  align-items: center;
  gap: 3cqw;
  padding: 2.4cqw 0;

  & + &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 16cqw;
    right: 0;
    height: 1px;
    background: ${props => props.$c.separator};
  }
`;

const TabBar = styled.div<{ $c: Palette }>`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 20cqw;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  padding-top: 2.4cqw;
  background: ${props => props.$c.bar};
  border-top: 1px solid ${props => props.$c.separator};
`;

const Tab = styled.div<{ $active: boolean; $c: Palette }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.8cqw;
  font-size: 2.45cqw;
  font-weight: 500;
  color: ${props => (props.$active ? props.theme.colors.primary : props.$c.secondary)};

  svg {
    width: 6.6cqw;
    height: 6.6cqw;
  }
`;

const tabIcons = [
  // Today
  <svg key="today" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
    <rect x="4" y="3" width="16" height="18" rx="3.5" />
    <rect x="7.5" y="6.5" width="9" height="6" rx="1.5" fill="currentColor" stroke="none" />
    <path d="M7.5 16h9M7.5 18.5h6" strokeLinecap="round" />
  </svg>,
  // Apps
  <svg key="apps" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
    <rect x="4" y="4" width="7" height="7" rx="2" />
    <rect x="13" y="4" width="7" height="7" rx="2" />
    <rect x="4" y="13" width="7" height="7" rx="2" />
    <rect x="13" y="13" width="7" height="7" rx="2" />
  </svg>,
  // Search
  <svg key="search" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round">
    <circle cx="10.5" cy="10.5" r="6" />
    <path d="m15 15 5 5" />
  </svg>,
  // Profile
  <svg key="profile" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
    <circle cx="12" cy="12" r="9" />
    <circle cx="12" cy="10" r="3.2" />
    <path d="M6.6 18.2c1.3-2 3.2-3 5.4-3s4.1 1 5.4 3" />
  </svg>,
];

export const InterfaceLayer: React.FC = () => {
  const { content, effectiveTheme, language } = useAppContext();
  const { projects, personalInfo, experience } = content;
  const ids = useMemo(() => projects.items.map(project => project.appStoreId), [projects.items]);
  const { apps } = useAppStoreData(ids);
  const c = effectiveTheme === 'dark' ? ios.dark : ios.light;
  const tr = language === 'tr';
  const locale = tr ? 'tr-TR' : 'en-US';

  const today = new Intl.DateTimeFormat(locale, { weekday: 'long', day: 'numeric', month: 'long' })
    .format(new Date())
    .toLocaleUpperCase(locale);
  const role = experience.items[0];
  const featured = projects.items[0];
  const category = (id: string) => {
    const genre = apps[id]?.genre;
    return genre ? (tr ? genresTr[genre] ?? genre : genre) : '';
  };
  const icon = (id: string) => (apps[id] ? { backgroundImage: `url(${apps[id].icon})` } : undefined);
  const open = tr ? 'AÇ' : 'OPEN';
  const tabs = tr ? ['Bugün', 'Uygulamalar', 'Ara', 'Profil'] : ['Today', 'Apps', 'Search', 'Profile'];

  return (
    <Screen $c={c}>
      <Header $c={c}>
        <div>
          <small>{today}</small>
          <h4>Portfolio</h4>
        </div>
        <img src={personalInfo.profileImage} alt="" />
      </Header>

      <Feature>
        <FeatureText>
          <small>{tr ? 'ŞU ANDA' : 'CURRENTLY'}</small>
          <strong>{role.company}</strong>
          {/* The "currently" overline already dates it. */}
          <span>{role.title}</span>
        </FeatureText>
        <FeatureApp>
          <Icon $size={9.6} style={icon(featured.appStoreId)} />
          <Meta>
            <strong>{featured.name}</strong>
            <span>{category(featured.appStoreId) || featured.description}</span>
          </Meta>
          <Get $fill="rgba(255, 255, 255, 0.92)">{open}</Get>
        </FeatureApp>
      </Feature>

      <SectionHead $c={c}>
        <strong>{tr ? 'Uygulamalar' : 'Apps'}</strong>
        <span>{tr ? 'Tümünü Gör' : 'See All'}</span>
      </SectionHead>

      {projects.items.slice(1, 5).map(project => (
        <AppRow key={project.appStoreId} $c={c}>
          <Icon $size={12.4} style={icon(project.appStoreId)} />
          <Meta $c={c}>
            <strong style={{ fontWeight: 500 }}>{project.name}</strong>
            <span>{category(project.appStoreId) || project.description}</span>
          </Meta>
          <Get $fill={c.fill}>{open}</Get>
        </AppRow>
      ))}

      <TabBar $c={c}>
        {tabs.map((label, index) => (
          <Tab key={label} $active={index === 0} $c={c}>
            {tabIcons[index]}
            {label}
          </Tab>
        ))}
      </TabBar>
    </Screen>
  );
};

/* ---------- Shared look for the structural layers ---------- */

const Plate = styled.div`
  position: absolute;
  inset: 0;
  padding: 7cqw 6cqw;
  display: flex;
  flex-direction: column;
  background: color-mix(in oklch, ${props => props.theme.colors.cardBackground}, ${props => props.theme.colors.text} ${props => (props.theme.name === 'dark' ? 6 : 0)}%);
  color: ${props => props.theme.colors.text};
  font-family: ${fonts.mono};
  overflow: hidden;
`;

const Tag = styled.div`
  font-size: 3.2cqw;
  color: ${props => props.theme.colors.textTertiary};
  margin-bottom: 6cqw;
`;

/* ---------- Architecture: MVVM-C ---------- */

const Node = styled.div<{ $accent?: boolean }>`
  padding: 3.4cqw 0;
  border-radius: 3cqw;
  text-align: center;
  font-size: 4cqw;
  border: 1px solid ${props => (props.$accent ? props.theme.colors.primary : props.theme.colors.borderStrong)};
  color: ${props => (props.$accent ? props.theme.colors.primaryText : props.theme.colors.text)};
  background: ${props => props.theme.colors.background};
`;

const Link = styled.div`
  width: 1px;
  height: 7cqw;
  margin: 0 auto;
  background: ${props => props.theme.colors.borderStrong};
`;

const Pair = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;

  i {
    width: 6cqw;
    height: 1px;
    background: ${props => props.theme.colors.borderStrong};
  }
`;

export const ArchitectureLayer: React.FC = () => (
  <Plate>
    <Tag>MVVM-C</Tag>
    <Node>View</Node>
    <Link />
    <Pair>
      <Node $accent>ViewModel</Node>
      <i />
      <Node>Model</Node>
    </Pair>
    <Link />
    <Node>Coordinator</Node>
    <Link />
    <Node>Router</Node>
  </Plate>
);

/* ---------- Data flow: Combine marbles ---------- */

const Stream = styled.div`
  margin-bottom: 9cqw;

  small {
    display: block;
    margin-bottom: 3cqw;
    font-size: 3.2cqw;
    color: ${props => props.theme.colors.textSecondary};
  }
`;

const Timeline = styled.div`
  position: relative;
  height: 5cqw;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    top: 50%;
    height: 1px;
    background: ${props => props.theme.colors.borderStrong};
  }
`;

const Marble = styled.span<{ $at: number; $accent?: boolean }>`
  position: absolute;
  top: 50%;
  left: ${props => props.$at}%;
  width: 5cqw;
  height: 5cqw;
  margin: -2.5cqw 0 0 -2.5cqw;
  border-radius: 50%;
  background: ${props => (props.$accent ? props.theme.colors.primary : props.theme.colors.text)};
`;

const streams: { label: string; marbles: number[]; accent?: number }[] = [
  { label: '$query', marbles: [8, 18, 26, 34, 62, 70] },
  { label: '.debounce(for: .milliseconds(300))', marbles: [46, 88] },
  { label: '.flatMap(api.search)', marbles: [56, 96], accent: 1 },
  { label: '.assign(to: \\.results)', marbles: [60, 98], accent: 1 },
];

export const DataFlowLayer: React.FC = () => (
  <Plate>
    <Tag>Combine</Tag>
    {streams.map(stream => (
      <Stream key={stream.label}>
        <small>{stream.label}</small>
        <Timeline>
          {stream.marbles.map((at, index) => (
            <Marble key={at} $at={at} $accent={stream.accent === index} />
          ))}
        </Timeline>
      </Stream>
    ))}
  </Plate>
);

/* ---------- Networking & storage ---------- */

const Request = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 2.6cqw 3.4cqw;
  border-radius: 2.6cqw;
  font-size: 3.4cqw;
  background: ${props => props.theme.colors.background};
  border: 1px solid ${props => props.theme.colors.border};

  b {
    font-weight: 500;
    color: ${props => props.theme.colors.success};
  }
`;

const Json = styled.pre`
  margin: 4cqw 0;
  font-family: inherit;
  font-size: 3.4cqw;
  line-height: 1.6;
  color: ${props => props.theme.colors.textSecondary};

  em {
    font-style: normal;
    color: ${props => props.theme.colors.primaryText};
  }
`;

const Store = styled.div`
  margin-top: auto;
  display: flex;
  align-items: center;
  gap: 3cqw;
  font-size: 3.4cqw;

  svg {
    width: 9cqw;
    height: 9cqw;
    color: ${props => props.theme.colors.textSecondary};
  }
`;

export const NetworkLayer: React.FC = () => (
  <Plate>
    <Tag>REST · Persistence</Tag>
    <Request>
      <span>GET /v1/apps</span>
      <b>200</b>
    </Request>
    <Json>
      {'{\n  '}
      <em>"id"</em>
      {': 388647888,\n  '}
      <em>"name"</em>
      {': "ATV",\n  '}
      <em>"platform"</em>
      {': "iOS"\n}'}
    </Json>
    <Request>
      <span>Alamofire · Moya</span>
    </Request>
    <Store>
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true">
        <ellipse cx="12" cy="5" rx="8" ry="3" />
        <path d="M4 5v14c0 1.66 3.58 3 8 3s8-1.34 8-3V5" />
        <path d="M4 12c0 1.66 3.58 3 8 3s8-1.34 8-3" />
      </svg>
      Core Data · Realm
    </Store>
  </Plate>
);
